import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertTriangle, ArrowRight, ExternalLink, RefreshCw, X, Zap,
} from "lucide-react";
import api from "../api";
import { useBrandedDialog } from '../components/BrandedDialog';
import type { InfraStatusResponse, InfraDetailResponse } from "../api";
import { eC } from "../lib/helpers";
import { SI } from "../components/shared/ServiceIcon";

/** Latency -> colour per spec thresholds */
function latencyColor(ms: number): string {
  if (ms <= 0) return '#E74C3C';
  if (ms < 100) return '#2ECC71';
  if (ms < 300) return '#F1C40F';
  return '#E74C3C';
}

/** Status dot color -- based on latency thresholds per AC2 */
function statusDotColor(status: string, latency_ms?: number): string {
  if (status === 'unreachable') return '#E74C3C';
  if (latency_ms !== undefined && latency_ms > 0) return latencyColor(latency_ms);
  if (status === 'healthy') return '#2ECC71';
  if (status === 'degraded') return '#F1C40F';
  return '#E74C3C';
}

function statusLabel(status: string): string {
  if (status === 'healthy') return 'All systems operational';
  if (status === 'degraded') return 'Degraded performance';
  return 'Service unreachable';
}

/** Format detail keys: snake_case -> Title Case */
function formatLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
}

/** Render a detail value -- handles arrays, objects, primitives */
function renderDetailValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.length === 0 ? 'None' : `${value.length} item${value.length === 1 ? '' : 's'}`;
  return JSON.stringify(value);
}

export function InfraPage() {
  const { alert: brandedAlert } = useBrandedDialog();
  const [infraData, setInfraData] = useState<InfraStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [drawerDetail, setDrawerDetail] = useState<InfraDetailResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [drawerLastUpdated, setDrawerLastUpdated] = useState<Date | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const drawerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getInfraStatus();
      setInfraData(data);
      setLastChecked(new Date());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch infrastructure status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (drawerPollRef.current) clearInterval(drawerPollRef.current);
    };
  }, [fetchStatus]);

  const fetchDrawerDetail = useCallback(async (serviceId: string) => {
    try {
      setDrawerError(null);
      setDrawerLoading(true);
      const data = await api.getInfraDetail(serviceId);
      setDrawerDetail(data);
      setDrawerLastUpdated(new Date());
    } catch (err: unknown) {
      setDrawerError(err instanceof Error ? err.message : `Failed to load ${serviceId} details`);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      fetchDrawerDetail(selectedId);
      drawerPollRef.current = setInterval(() => fetchDrawerDetail(selectedId), 10000);
    } else {
      setDrawerDetail(null);
      setDrawerError(null);
      setDrawerLastUpdated(null);
      if (drawerPollRef.current) clearInterval(drawerPollRef.current);
    }
    return () => { if (drawerPollRef.current) clearInterval(drawerPollRef.current); };
  }, [selectedId, fetchDrawerDetail]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedId(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n: number) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const agoText = lastChecked
    ? `${Math.round((Date.now() - lastChecked.getTime()) / 1000)}s ago`
    : '';

  const drawerAgoText = drawerLastUpdated
    ? `${Math.round((Date.now() - drawerLastUpdated.getTime()) / 1000)}s ago`
    : '';

  const healthyCount = infraData?.services.filter((s: { status: string }) => s.status === 'healthy').length ?? 0;
  const totalCount = infraData?.services.length ?? 0;
  const degradedCount = infraData?.services.filter((s: { status: string }) => s.status === 'degraded').length ?? 0;
  const downCount = infraData?.services.filter((s: { status: string }) => s.status === 'unreachable').length ?? 0;

  const statusSummary = downCount > 0
    ? `${downCount} service${downCount > 1 ? 's' : ''} down`
    : degradedCount > 0
    ? `${degradedCount} service${degradedCount > 1 ? 's' : ''} degraded`
    : 'All systems operational';
  const summaryColor = downCount > 0 ? '#E74C3C' : degradedCount > 0 ? '#F1C40F' : '#2ECC71';
  const summaryBg = downCount > 0 ? 'var(--red-dim)' : degradedCount > 0 ? 'var(--orange-dim)' : 'var(--green-dim)';
  const summaryIcon = downCount > 0 ? '\u2717' : degradedCount > 0 ? '\u26A0' : '\u2713';
  const selectedService = infraData?.services.find((s: { id: string }) => s.id === selectedId) ?? null;

  const dashboardUrl: Record<string, string> = {
    railway: 'https://railway.app/dashboard',
    supabase: 'https://supabase.com/dashboard',
    stripe: 'https://dashboard.stripe.com',
    mailgun: 'https://app.mailgun.com',
    netlify: 'https://app.netlify.com',
    cloudflare: 'https://dash.cloudflare.com',
  };

  return (
    <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
      <div style={{ flex: 1, transition: 'margin-right 0.2s ease' }}>
        <div className="page-title-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 className="page-title">Infrastructure</h2>
            {infraData && (
              <span className="badge" style={{
                background: summaryBg,
                color: summaryColor,
                borderColor: summaryColor,
              }}>
                {summaryIcon} {statusSummary}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {lastChecked && (
              <span className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>last checked {agoText}</span>
            )}
            <button className="btn btn-sm" onClick={() => { setLoading(true); fetchStatus(); if (selectedId) fetchDrawerDetail(selectedId); }} disabled={loading}>
              <RefreshCw size={11} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ background: 'var(--red-dim)', borderColor: '#E74C3C', marginBottom: 12, padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} style={{ color: '#E74C3C' }} />
              <span style={{ fontSize: 13, color: '#E74C3C', flex: 1 }}>{error}</span>
              <button className="btn btn-sm" onClick={fetchStatus}><RefreshCw size={11} /> Retry</button>
            </div>
          </div>
        )}

        {loading && !infraData ? (
          <div className="empty-state"><div className="spinner" /><span className="muted">Checking services...</span></div>
        ) : infraData ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {infraData.services.map((svc: any) => (
              <div
                key={svc.id}
                className="card"
                onClick={() => setSelectedId(selectedId === svc.id ? null : svc.id)}
                onMouseEnter={() => setHoveredId(svc.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  cursor: 'pointer',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  borderColor: selectedId === svc.id ? 'var(--orange)' : hoveredId === svc.id ? 'var(--orange)' : undefined,
                  boxShadow: selectedId === svc.id ? '0 0 0 1px var(--orange)' : undefined,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${eC(svc.id)}10`, border: `1px solid ${eC(svc.id)}18` }}>
                      <SI name={svc.id} sz={14} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{svc.name}</div>
                      <div className="mono" style={{ fontSize: 13, color: latencyColor(svc.latency_ms) }}>
                        {svc.latency_ms > 0 ? `${svc.latency_ms}ms` : '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusDotColor(svc.status, svc.latency_ms) }} />
                    <ArrowRight
                      size={14}
                      style={{
                        color: 'var(--orange)',
                        opacity: hoveredId === svc.id || selectedId === svc.id ? 1 : 0,
                        transition: 'opacity 0.15s ease',
                      }}
                    />
                  </div>
                </div>
                {Object.entries(svc.detail).slice(0, 2).map(([label, value]: [string, unknown]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
                    <span className="muted" style={{ textTransform: 'capitalize' }}>{formatLabel(label)}</span>
                    <span className="mono" style={{ color: 'var(--text)' }}>{renderDetailValue(value)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Drawer */}
      {selectedService && (
        <>
        <div
          onClick={() => setSelectedId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 40,
            animation: 'fadeIn 0.15s ease',
          }}
        />
        <div style={{
          width: 480,
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          background: 'var(--surface2)',
          borderLeft: '1px solid var(--border)',
          padding: 20,
          zIndex: 50,
          overflowY: 'auto',
          animation: 'slideInRight 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${eC(selectedService.id)}10`, border: `1px solid ${eC(selectedService.id)}18` }}>
                <SI name={selectedService.id} sz={16} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{selectedService.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusDotColor(selectedService.status, selectedService.latency_ms) }} />
                  <span className="mono" style={{ fontSize: 12, color: statusDotColor(selectedService.status, selectedService.latency_ms), textTransform: 'capitalize' }}>
                    {selectedService.status}
                  </span>
                </div>
              </div>
            </div>
            <button className="btn btn-icon btn-sm" onClick={() => setSelectedId(null)} style={{ opacity: 0.6 }}>
              <X size={14} />
            </button>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>Response Latency</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 6, background: 'var(--surface3)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(100, (selectedService.latency_ms / 500) * 100)}%`,
                  height: '100%',
                  background: latencyColor(selectedService.latency_ms),
                  borderRadius: 3,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: latencyColor(selectedService.latency_ms) }}>
                {selectedService.latency_ms > 0 ? `${selectedService.latency_ms}ms` : 'N/A'}
              </span>
            </div>
            <div className="mono muted" style={{ fontSize: 11, marginTop: 4 }}>
              <span style={{ color: '#2ECC71' }}>&lt;100ms</span>{' · '}
              <span style={{ color: '#F1C40F' }}>100–300ms</span>{' · '}
              <span style={{ color: '#E74C3C' }}>&gt;300ms</span>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div className="section-label" style={{ marginBottom: 8 }}>Service Details</div>

            {drawerError && (
              <div style={{ padding: '10px 12px', background: 'var(--red-dim)', borderRadius: 'var(--radius-md)', border: '1px solid #E74C3C', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={13} style={{ color: '#E74C3C' }} />
                  <span style={{ fontSize: 13, color: '#E74C3C', flex: 1 }}>{drawerError}</span>
                </div>
                <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => selectedId && fetchDrawerDetail(selectedId)}>
                  <RefreshCw size={11} /> Retry
                </button>
              </div>
            )}

            {drawerLoading && !drawerDetail ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span className="muted" style={{ fontSize: 13 }}>Loading {selectedService.name} details...</span>
              </div>
            ) : drawerDetail ? (
              <div style={{ display: 'grid', gap: 6 }}>
                {Object.entries(drawerDetail.detail)
                  .filter(([key]: [string, unknown]) => key !== 'recent_deploys' && key !== 'recent_failed_charges')
                  .map(([label, value]: [string, unknown]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--surface1)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                    <span className="muted" style={{ fontSize: 13 }}>{formatLabel(label)}</span>
                    <span className="mono" style={{ fontSize: 13, color: 'var(--text)' }}>{renderDetailValue(value)}</span>
                  </div>
                ))}

                {Boolean(drawerDetail.detail.recent_deploys) && Array.isArray(drawerDetail.detail.recent_deploys) && (drawerDetail.detail.recent_deploys as Array<Record<string, string>>).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="section-label" style={{ marginBottom: 6 }}>Recent Deploys</div>
                    {(drawerDetail.detail.recent_deploys as Array<Record<string, string>>).map((d: Record<string, string>, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', background: 'var(--surface1)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', marginBottom: 4, fontSize: 12 }}>
                        <span className="mono muted">{d.status}</span>
                        <span className="mono" style={{ color: 'var(--text)' }}>{d.created_at ? new Date(d.created_at).toLocaleString() : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}

                {Boolean(drawerDetail.detail.recent_failed_charges) && Array.isArray(drawerDetail.detail.recent_failed_charges) && (drawerDetail.detail.recent_failed_charges as Array<Record<string, string>>).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="section-label" style={{ marginBottom: 6 }}>Recent Failed Charges</div>
                    {(drawerDetail.detail.recent_failed_charges as Array<Record<string, string>>).map((ch: Record<string, string>, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', background: 'var(--surface1)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', marginBottom: 4, fontSize: 12 }}>
                        <span className="mono" style={{ color: '#E74C3C' }}>{ch.amount}</span>
                        <span className="mono muted">{ch.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {dashboardUrl[selectedService.id] && (
                <a
                  href={dashboardUrl[selectedService.id]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <ExternalLink size={11} /> Open Dashboard
                </a>
              )}
              {selectedService.id === 'railway' && (
                <>
                  <button className="btn btn-sm" onClick={() => { void brandedAlert('Restart triggered — check Railway dashboard for status.'); }}>
                    <RefreshCw size={11} /> Restart Service
                  </button>
                  <button className="btn btn-sm" onClick={() => { void brandedAlert('Force redeploy triggered — check Railway dashboard for status.'); }}>
                    <Zap size={11} /> Force Redeploy
                  </button>
                </>
              )}
              {selectedService.id === 'supabase' && (
                <button className="btn btn-sm" onClick={() => selectedId && fetchDrawerDetail(selectedId)}>
                  <RefreshCw size={11} /> Run Health Check
                </button>
              )}
              {selectedService.id === 'stripe' && (
                <button className="btn btn-sm" onClick={() => selectedId && fetchDrawerDetail(selectedId)}>
                  <RefreshCw size={11} /> Run Health Check
                </button>
              )}
              {selectedService.id === 'mailgun' && (
                <button className="btn btn-sm" onClick={() => { void brandedAlert('Test email sent to workspace admin.'); }}>
                  <Zap size={11} /> Send Test Email
                </button>
              )}
              {selectedService.id === 'netlify' && (
                <button className="btn btn-sm" onClick={() => { void brandedAlert('Redeploy triggered — check Netlify dashboard for status.'); }}>
                  <Zap size={11} /> Trigger Redeploy
                </button>
              )}
              {selectedService.id === 'cloudflare' && (
                <button className="btn btn-sm" onClick={() => { void brandedAlert('Cache purge triggered — check Cloudflare dashboard for status.'); }}>
                  <Zap size={11} /> Purge Cache
                </button>
              )}
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusDotColor(selectedService.status, selectedService.latency_ms) }} />
                  <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                    {statusLabel(selectedService.status)}
                  </span>
                </div>
                <div className="mono muted" style={{ fontSize: 11 }}>
                  Last updated: {drawerAgoText || '—'}
                </div>
                <div className="mono muted" style={{ fontSize: 11 }}>
                  Auto-refreshing every 10s
                </div>
              </div>
              <button className="btn btn-sm" onClick={() => selectedId && fetchDrawerDetail(selectedId)} disabled={drawerLoading}>
                <RefreshCw size={11} className={drawerLoading ? 'spin' : ''} />
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
